import React, { useState, useRef, useEffect } from "react";

/**
 * A searchable dropdown that replaces <select> for long option lists.
 * Props:
 *   value       - currently selected value
 *   onChange    - (value) => void
 *   options     - [{ value, label }]
 *   placeholder - placeholder text when nothing is selected (e.g. "All Projects")
 *   className   - optional extra className on the wrapper
 *   disabled    - disable the dropdown
 *   searchPlaceholder - placeholder for the search input
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select...",
  className = "",
  disabled = false,
  searchPlaceholder = "Search...",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Auto-focus search input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  const selectedLabel =
    options.find((o) => o.value === value)?.label || placeholder;

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div
      ref={ref}
      className={`searchable-select ${className}`}
      style={{ position: "relative" }}
    >
      {/* Trigger */}
      <div
        className={`searchable-select-trigger ${disabled ? "disabled" : ""} ${open ? "open" : ""}`}
        onClick={() => !disabled && setOpen(!open)}
      >
        <span className={value ? "ss-value" : "ss-placeholder"}>
          {selectedLabel}
        </span>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          style={{
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            flexShrink: 0,
          }}
        >
          <path
            d="M3 4.5L6 7.5L9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="searchable-select-dropdown">
          <div className="ss-search-wrap">
            <input
              ref={inputRef}
              type="text"
              className="ss-search-input"
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="ss-options">
            {filtered.length === 0 ? (
              <div className="ss-no-results">No results found</div>
            ) : (
              filtered.map((opt) => (
                <div
                  key={opt.value}
                  className={`ss-option ${opt.value === value ? "selected" : ""}`}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setQuery("");
                  }}
                >
                  {opt.label}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
